import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, ShoppingCart, ShieldCheck, Zap } from "lucide-react";

export const Route = createFileRoute("/revenda-iptv/plano")({
  head: () => ({
    meta: [
      { title: "Planos de Revenda IPTV Pós-Pago - Pague pelo Uso | P2 PRO" },
      { name: "description", content: "Conheça os planos de revenda IPTV mais flexíveis do Brasil. Modelo pós-pago: sem compra de créditos antecipada, investimento baixo e faturamento imediato." },
      { name: "keywords", content: "plano revendedor iptv, tabela revenda iptv, custo credito iptv, iptv pos-pago planos" },
    ],
  }),
  component: () => (
    <SEOPage
      variant="split"
      hideBenefits={true}
      showPriceTable={true}
      category="Tabela de Investimento"
      title="PLANOS DE REVENDA IPTV: O PODER DO PÓS-PAGO"
      subtitle="Esqueça o estoque parado. Conheça a estrutura de custos que permite você lucrar antes mesmo de pagar."
      introParagraphs={[
        "No mercado tradicional, um 'Plano de Revenda' significa comprar um lote de 50 ou 100 créditos e rezar para vender tudo antes do vencimento. Na P2 PRO, subvertemos essa lógica com o modelo Pós-Pago Profissional. Nossos planos são baseados na sua capacidade de escala, permitindo que você gerencie seu fluxo de caixa com inteligência absoluta.",
        "Ao escolher um plano P2 PRO, você não está comprando um produto físico, mas sim o acesso à maior infraestrutura de streaming da América Latina. O custo por ativação é descontado apenas no momento em que você fecha uma venda, garantindo que seu investimento inicial seja direcionado para o que realmente importa: o crescimento da sua marca e a atração de novos clientes.",
        "Seja você um empreendedor solo ou uma empresa com centenas de sub-revendedores, nossos níveis de parceria garantem que sua margem de lucro seja sempre a maior do setor. Aqui, o lucro é seu e a tecnologia é nossa."
      ]}
      sections={[
        {
          title: "A Logística Financeira Inteligente",
          type: "grid",
          content: (
            <>
              {[
                { title: "Ativação em Segundos", desc: "Seu cliente paga, você clica no painel e o sinal libera na hora. Sem burocracia ou esperas.", icon: <Zap className="text-electric" /> },
                { title: "Segurança de Saldo", desc: "Seus créditos não expiram. Eles ficam seguros na nuvem até serem utilizados em uma venda real.", icon: <ShieldCheck className="text-green-400" /> },
                { title: "Dashboard Integrado", desc: "Acompanhe cada centavo que entra e sai do seu negócio com relatórios financeiros automáticos.", icon: <ShoppingCart className="text-purple-400" /> }
              ].map((item, i) => (
                <div key={i} className="p-8 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all text-center">
                  <div className="flex justify-center mb-4 text-3xl">{item.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </>
          )
        },
        {
          title: "Por que nosso plano é diferente?",
          type: "featured",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center text-white">
              <div className="space-y-6">
                <p className="text-lg leading-relaxed">
                  A maioria dos servidores IPTV ganha dinheiro na venda do crédito para o revendedor. Nós só ganhamos quando você vende para o cliente final.
                </p>
                <div className="space-y-4">
                  {[
                    "Menor custo por ativação Full HD/4K do Brasil",
                    "Acesso gratuito a scripts de tráfego pago",
                    "Suporte de engenharia para grandes redes",
                    "Painel Master com limite de sub-revenda"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-electric" />
                      <span className="font-bold text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white/5 p-8 rounded-[2rem] border border-white/10 text-center">
                <div className="text-4xl font-black italic mb-2 tracking-tighter">MARGEM DE 400%</div>
                <p className="text-white/60 text-sm">Nossa estrutura de custos permite que você venda planos de R$ 35,00 a R$ 60,00 tendo um custo de ativação fixo extremamente baixo.</p>
              </div>
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Flexibilidade Total", description: "Não exigimos volume mínimo mensal para quem está começando." },
        { title: "Tecnologia P2P Nativa", description: "Evite bloqueios de operadoras com nossa tecnologia anti-travamento." },
        { title: "Suporte VIP", description: "Fale direto com quem entende da parte técnica e comercial do negócio." }
      ]}
      howItWorks={[
        { title: "Escolha", description: "Defina seu nível de parceria no momento do cadastro." },
        { title: "Divulgue", description: "Use nossas ferramentas para atrair clientes interessados." },
        { title: "Venda", description: "O cliente paga e você ativa a linha dele instantaneamente." },
        { title: "Escala", description: "Conforme seu volume cresce, seu custo cai e seu lucro sobe." }
      ]}
      diferenciais={[
        { title: "Aplicativos Próprios", description: "Instale o sinal nos melhores apps do mercado com DNS exclusivo." },
        { title: "Relatórios de VOD", description: "Saiba quais conteúdos seus clientes mais assistem e foque neles." },
        { title: "Backup de Dados", description: "Sua carteira de clientes segura com redundância em servidores Cloud." }
      ]}
      faq={[
        { question: "Tem taxa de adesão?", answer: "Trabalhamos com uma taxa mínima de ativação de painel que é 100% revertida em facilidades para você." },
        { question: "Como renovo os créditos?", answer: "Tudo é feito via Pix direto no painel, com liberação automática e instantânea." },
        { question: "Posso mudar de plano?", answer: "Sim, conforme sua carteira de clientes cresce, você sobe de nível e ganha descontos progressivos." }
      ]}
      breadcrumbs={[
        { label: "Revenda IPTV", href: "/revenda-iptv" },
        { label: "Planos", href: "/revenda-iptv/plano" }
      ]}
      relatedArticles={[
        { title: "Quanto Ganha um Revendedor", href: "/revenda-iptv/quanto-ganha" },
        { title: "Como Ser um Revendedor", href: "/revenda-iptv/como-ser" },
        { title: "Revenda para Iniciantes", href: "/revenda-iptv/iniciantes" }
      ]}
    />
  ),
});
